import { useEffect, useRef, useState } from "react";

/**
 * Click-to-edit text. Shows a value with a small Edit affordance; commits on
 * blur or Enter, abandons on Escape. Used by every chip and card row on the
 * connection profile so editing never means opening a modal.
 */
export default function InlineEditableField({
  value = "",
  onSave,
  label,
  placeholder = "Add",
  type = "text",
  multiline = false,
  disabled = false,
  className = "",
  children,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = async () => {
    setEditing(false);
    const next = draft.trim();
    if (next === (value || "").trim()) return;
    // Only enter the saving state for a genuinely async save, so a synchronous
    // handler causes no extra render.
    const result = onSave?.(next);
    if (!result || typeof result.then !== "function") return;
    setSaving(true);
    try {
      await result;
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setDraft(value);
      setEditing(false);
    }
    if (event.key === "Enter" && !multiline) {
      event.preventDefault();
      commit();
    }
  };

  if (editing) {
    const Field = multiline ? "textarea" : "input";
    return (
      <Field
        ref={inputRef}
        className={`inline-edit-input ${className}`.trim()}
        type={multiline ? undefined : type}
        value={draft}
        aria-label={label}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <button
      type="button"
      className={`inline-edit-trigger${value ? "" : " is-empty"} ${className}`.trim()}
      onClick={() => !disabled && setEditing(true)}
      disabled={disabled || saving}
      aria-label={value ? `Edit ${label}` : `Add ${label}`}
    >
      {children || <span className="inline-edit-value">{value || placeholder}</span>}
      <span className="inline-edit-hint" aria-hidden="true">
        {saving ? "…" : value ? "Edit" : "+"}
      </span>
    </button>
  );
}
